import { Breadcrumb } from '@/app/components/Breadcrumb'
import { Footer } from '@/app/components/Footer'
import { useCourses } from '@/app/hooks/data-hooks/useCourses'
import { MAX_COURSE_FILTER_PRICE, ROUTES } from '@/app/lib/constants'
import { Header } from '@/app/modules/shared/components/Header'
import { ICourseFilters } from '@/app/services/courses'
import { ContractType } from '@/types/agreement'
import { IPaginationParams, PaginatedQueryResponse } from '@/types/api'
import { ICourse } from '@/types/course'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { MdOutlineTune } from 'react-icons/md'
import { InfiniteData } from 'react-query'
import { CourseFiltersModal, CoursesList } from '../../course/components'
import { NoCoursesMessage } from '../../course/components/NoCoursesMessage'
import { Button } from '../../shared/components'

type SearchCoursePageProps = {
  filterTitle: string
  initialCoursesResponse: InfiniteData<PaginatedQueryResponse<ICourse>>
  initialQueryParams: ICourseFilters & IPaginationParams
}

export function SearchCoursesPage({
  filterTitle,
  initialCoursesResponse,
  initialQueryParams,
}: SearchCoursePageProps) {
  const t = useTranslations('SearchCoursesPage')
  const router = useRouter()

  const [queryParams, setQueryParams] = useState(initialQueryParams)
  const { page, limit, ...filters } = { ...initialQueryParams, ...queryParams }

  const [isFilterModalOpen, setFilterModalOpen] = useState(false)

  const queryFilters = {
    ...filters,
  }

  const {
    data: { pages: pages = [] } = {},
    fetchNextPage,
    hasNextPage,
  } = useCourses(
    queryFilters,
    { page, limit },
    {
      qualityScore: 'desc',
      random: 'desc',
    },
    {
      retry: false,
      initialData: initialCoursesResponse,
    }
  )

  const filteredCourses = pages.map?.(({ data }) => data).flat() ?? []
  const totalCourses = pages[0]?.metadata.total ?? 0

  const defaultValues = {
    categories: Array.isArray(filters.categories)
      ? filters.categories
      : [filters.categories].filter(Boolean),
    subCategories: Array.isArray(filters.subCategories)
      ? filters.subCategories
      : [filters.subCategories].filter(Boolean),
    skills: Array.isArray(filters.skills)
      ? filters.skills
      : [filters.skills].filter(Boolean),
    paymentOptions: Array.isArray(filters.paymentOptions)
      ? filters.paymentOptions
      : [filters.paymentOptions].filter(Boolean),
    educationProvider: Array.isArray(filters.educationProvider)
      ? filters.educationProvider
      : [filters.educationProvider].filter(Boolean),
    type: filters.type ? filters.type : [],
    maxDuration: filters.maxDuration ? filters.maxDuration : null,
    agreementType:
      filters.agreementType &&
      Object.values(ContractType).includes(filters.agreementType)
        ? filters.agreementType
        : '',
    budget:
      filters.minPrice || filters.maxPrice
        ? {
            ...(filters.minPrice && { min: +filters.minPrice }),
            ...(filters.maxPrice && { max: +filters.maxPrice }),
          }
        : { min: 0, max: MAX_COURSE_FILTER_PRICE },
  }

  useEffect(() => {
    setQueryParams(router.query)
  }, [router.query])

  return (
    <>
      <Head>
        <title>Search - Courses</title>
      </Head>
      <Header />

      <main>
        <div className="container flex flex-col gap-4 pb-28">
          <div className="flex items-center pt-8">
            <Breadcrumb
              items={[
                ...(filterTitle
                  ? [{ path: '', label: filterTitle }]
                  : [{ path: '', label: t('search_results') }]),
              ]}
            />
            <Button
              color="white"
              size="small"
              onClick={() => setFilterModalOpen(true)}
              className="ml-auto"
            >
              <div className="flex flex-row items-center gap-2">
                <MdOutlineTune className="h-4 w-4" />
                <span className="hidden text-sm sm:block">{t('filters')}</span>
              </div>
            </Button>
          </div>

          {totalCourses === 0 && (
            <NoCoursesMessage
              onClearFiltersClick={() => router.push(router.pathname)}
            />
          )}

          {/** TODO: Add isLoading */}
          <CoursesList
            title={t('x_courses_found', { count: totalCourses })}
            courses={filteredCourses}
            loadMore={hasNextPage ? fetchNextPage : undefined}
          />
        </div>
      </main>

      <Footer />
      <CourseFiltersModal
        open={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onClear={async () => {
          await router.push(router.pathname)
          setFilterModalOpen(false)
        }}
        onSubmit={async (data) => {
          const query = Object.entries(data).reduce((acc, [key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
              return { ...acc, [key]: value.map((v) => v.id) }
            }

            if (value?.id) {
              return { ...acc, [key]: value.id }
            }

            if (key === 'budget') {
              return {
                ...acc,
                ...(value.min && { minPrice: value.min }),
                ...(value.max &&
                  value.max !== MAX_COURSE_FILTER_PRICE && {
                    maxPrice: value.max,
                  }),
              }
            }

            if (
              key === 'agreementType' &&
              Object.values(ContractType).includes(value)
            ) {
              return {
                ...acc,
                [key]: value,
              }
            }

            if (typeof value === 'string' && value.trim()) {
              return { ...acc, [key]: value.trim() }
            }

            return acc
          }, {})

          await router.push({
            pathname: ROUTES.search,
            query: {
              // keep "term" if it exists
              ...(router.query.term && { term: router.query.term }),
              ...query,
            },
          })

          setFilterModalOpen(false)
        }}
        defaultValues={defaultValues}
      />
    </>
  )
}
